import { useParams, Link } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, MapPin,
  Users, Star, Calendar, Mail,
  FileText, BarChart3, Zap
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getLocationByStateAndSlug, getLocationsByState, type LocationData } from '../../data/locations';
import { INDUSTRIES } from '../../data/industries';

// Generate consistent pseudo-random features based on location name
function getLocationFeatures(loc: LocationData) {
  const features = [
    { icon: Zap, title: 'Instant Auto-Emails', desc: `Every applicant in ${loc.city} gets an automatic response within seconds of applying.` },
    { icon: Calendar, title: 'Google Calendar Scheduling', desc: `Candidates self-schedule interviews through your calendar. No back-and-forth emails.` },
    { icon: FileText, title: 'Custom Application Forms', desc: `Build branded forms tailored to ${loc.city} job market with drag-and-drop simplicity.` },
    { icon: BarChart3, title: 'Applicant Tracking Dashboard', desc: `Track every candidate in ${loc.city} from application to hire in one clean dashboard.` },
    { icon: Mail, title: 'Email Campaigns', desc: `Send targeted email campaigns to candidates across ${loc.stateAbbr} to keep them engaged.` },
    { icon: Users, title: 'Team Collaboration', desc: `Your entire ${loc.city} hiring team can review applicants and leave notes together.` },
  ];
  return features;
}

export default function DynamicLocationPage() {
  const { state, slug } = useParams<{ state: string; slug: string }>();
  const location = state && slug ? getLocationByStateAndSlug(state, slug) : undefined;

  if (!location) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-44 lg:pb-28">
          <div className="container-custom text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Location Not Found</h1>
            <p className="text-gray-600 mb-8">
              We could not find this location. Browse our available locations below.
            </p>
            <Link to="/locations" className="btn-primary">
              View All Locations
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const features = getLocationFeatures(location);
  const nearby = getLocationsByState(location.stateAbbr)
    .filter((l) => l.slug !== location.slug && l.type === location.type)
    .slice(0, 6);
  const typeLabel = location.type === 'county' ? location.city : location.city;
  const fullName = `${typeLabel}, ${location.stateAbbr}`;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <Link
              to={`/locations/${state}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              {location.state} Locations
            </Link>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-8">
              <MapPin className="w-4 h-4" />
              {fullName}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              Hire faster in{' '}
              <span className="gradient-text">{typeLabel}</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {location.description} iHireFast helps local businesses automate their hiring with
              custom forms, applicant tracking, and interview scheduling.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a
                href="https://app.ihirefast.com"
                className="btn-primary text-base px-8 py-4 w-full sm:w-auto group"
              >
                Start Hiring in {location.city.split(' ')[0]}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="btn-secondary text-base px-8 py-4 w-full sm:w-auto"
              >
                Talk to Sales
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto">
              {[
                { label: 'Avg. Time to Hire', value: '3 days' },
                { label: 'Response Rate', value: '94%' },
                { label: 'Local Businesses', value: '500+' },
                { label: 'Applicants Processed', value: '10K+' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Everything you need to hire in {typeLabel}
            </h2>
            <p className="text-lg text-gray-600">
              iHireFast gives {location.city} businesses the tools to automate recruiting from application to interview.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((feature, i) => (
              <div key={i} className="card p-6 group hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-600 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Start hiring in {typeLabel} in minutes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '01', title: 'Create Your Account', desc: 'Sign up free with Google in 30 seconds. Set up your company profile and branding.', icon: Users },
              { step: '02', title: 'Build & Share Your Form', desc: 'Create a custom application form and share the link on job boards, your website, or social media.', icon: FileText },
              { step: '03', title: 'Hire Automatically', desc: 'Applicants get auto-emailed instantly and self-schedule interviews via Google Calendar. You just show up and hire.', icon: Calendar },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-primary-600" />
                </div>
                <div className="text-xs font-bold text-primary-600 mb-2">STEP {step.step}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Industries
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Hire by industry in {typeLabel}
            </h2>
            <p className="text-lg text-gray-600">
              iHireFast works for any industry. Explore industry-specific hiring pages for {fullName}.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 max-w-4xl mx-auto">
            {INDUSTRIES.map((ind) => (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}/${state}/${location.slug}`}
                className="card p-4 group hover:shadow-md transition-all duration-300 text-center"
              >
                <div className="text-2xl mb-2">{ind.icon}</div>
                <h4 className="text-xs font-semibold text-gray-900 group-hover:text-primary-600 transition-colors leading-tight">
                  {ind.name}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <blockquote className="text-xl sm:text-2xl font-medium text-gray-900 leading-relaxed mb-8">
              "We went from spending hours chasing applicants to having interviews booked automatically. iHireFast changed how we hire in {location.state}."
            </blockquote>
            <div>
              <div className="font-semibold text-gray-900">Local Business Owner</div>
              <div className="text-sm text-gray-500">{fullName}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to hire faster in {typeLabel}?
            </h2>
            <p className="text-lg text-primary-100 mb-10 max-w-xl mx-auto">
              Join businesses in {fullName} using iHireFast to streamline recruiting. Free to start, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.ihirefast.com"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Locations */}
      {nearby.length > 0 && (
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="container-custom">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Nearby locations</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {nearby.map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/locations/${state}/${loc.slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 transition-all"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {loc.city}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
